import { useState } from 'react'

export type TimePeriodValue = string // 'all' | 'this-week' | 'this-month' | 'ytd' | etc.

interface TimePeriodFilterProps {
  value: string
  onChange: (value: string) => void
}

/**
 * Shared time period filter dropdown used across all pages.
 * Includes relative presets (auto-computed) + rolling 4 quarters + years + custom date range.
 */
export function TimePeriodFilter({ value, onChange }: TimePeriodFilterProps) {
  const [showCustom, setShowCustom] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  // Generate last 4 rolling quarters dynamically
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1 // 1-4

  const rollingQuarters: { label: string; value: string }[] = []
  let qYear = currentYear
  let qNum = currentQuarter
  for (let i = 0; i < 4; i++) {
    rollingQuarters.push({ label: `Q${qNum} ${qYear}`, value: `Q${qNum}-${qYear}` })
    qNum--
    if (qNum === 0) { qNum = 4; qYear-- }
  }

  // Generate years dynamically (current year + previous year)
  const years = [currentYear, currentYear - 1]

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value
    if (val === 'custom') {
      setShowCustom(true)
    } else {
      setShowCustom(false)
      onChange(val)
    }
  }

  const applyCustomRange = () => {
    if (customFrom && customTo) {
      onChange(`custom:${customFrom}:${customTo}`)
      setShowCustom(false)
    }
  }

  const getDisplayLabel = () => {
    if (value.startsWith('custom:')) {
      const [, from, to] = value.split(':')
      const formatShort = (d: string) => {
        const [y, m, day] = d.split('-')
        return `${m}/${day}`
      }
      return `${formatShort(from)} – ${formatShort(to)}`
    }
    return null // Use native select display
  }

  const isActive = value !== 'all'

  return (
    <div className="relative inline-flex items-center gap-2">
      <select
        value={value.startsWith('custom:') ? 'custom' : value}
        onChange={handleChange}
        className={`px-4 py-2 rounded-lg border transition-colors text-sm ${
          isActive
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium'
            : 'border-[var(--color-border)] bg-white text-[var(--color-text-secondary)]'
        }`}
        style={{ height: '44px' }}
      >
        <option value="all">All Time</option>
        <optgroup label="Relative">
          <option value="this-week">This Week</option>
          <option value="this-month">This Month</option>
          <option value="last-30">Last 30 Days</option>
          <option value="this-quarter">This Quarter</option>
          <option value="ytd">Year to Date</option>
          <option value="last-quarter">Last Quarter</option>
          <option value="last-month">Last Month</option>
        </optgroup>
        <optgroup label="Year">
          {years.map(y => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </optgroup>
        <optgroup label="Quarter">
          {rollingQuarters.map(q => (
            <option key={q.value} value={q.value}>{q.label}</option>
          ))}
        </optgroup>
        <optgroup label="Custom">
          <option value="custom">Custom Range...</option>
        </optgroup>
      </select>

      {/* Custom range display label */}
      {value.startsWith('custom:') && (
        <span className="text-xs text-[var(--color-accent)] font-medium">
          {getDisplayLabel()}
        </span>
      )}

      {/* Custom range picker */}
      {showCustom && (
        <div className="absolute top-full mt-1 right-0 z-50 bg-white border border-[var(--color-border)] rounded-lg shadow-lg p-3 flex items-center gap-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="px-2 py-1.5 text-sm border border-[var(--color-border)] rounded"
          />
          <span className="text-xs text-[var(--color-text-muted)]">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="px-2 py-1.5 text-sm border border-[var(--color-border)] rounded"
          />
          <button
            onClick={applyCustomRange}
            disabled={!customFrom || !customTo}
            className="px-3 py-1.5 text-xs font-medium rounded bg-[var(--color-accent)] text-white disabled:opacity-50"
          >
            Apply
          </button>
          <button
            onClick={() => setShowCustom(false)}
            className="px-2 py-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Shared utility: resolves a time period value to a { start, end } date range.
 * Returns null for 'all' (no filtering).
 */
export function getTimePeriodRange(period: string): { start: string; end: string } | null {
  if (period === 'all') return null

  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() // 0-indexed
  const day = now.getDate()
  const dayOfWeek = now.getDay() // 0=Sun

  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  // Custom range
  if (period.startsWith('custom:')) {
    const [, start, end] = period.split(':')
    return { start, end }
  }

  switch (period) {
    case 'this-week': {
      const startOfWeek = new Date(year, month, day - dayOfWeek)
      return { start: fmt(startOfWeek), end: fmt(now) }
    }
    case 'this-month': {
      return { start: `${year}-${String(month + 1).padStart(2, '0')}-01`, end: fmt(now) }
    }
    case 'last-month': {
      const lastMonth = month === 0 ? 11 : month - 1
      const lastMonthYear = month === 0 ? year - 1 : year
      const lastDay = new Date(lastMonthYear, lastMonth + 1, 0).getDate()
      return {
        start: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
        end: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      }
    }
    case 'last-30': {
      const thirtyAgo = new Date(year, month, day - 30)
      return { start: fmt(thirtyAgo), end: fmt(now) }
    }
    case 'this-quarter': {
      const qStart = Math.floor(month / 3) * 3
      return { start: `${year}-${String(qStart + 1).padStart(2, '0')}-01`, end: fmt(now) }
    }
    case 'last-quarter': {
      const currentQ = Math.floor(month / 3)
      const prevQ = currentQ === 0 ? 3 : currentQ - 1
      const prevQYear = currentQ === 0 ? year - 1 : year
      const qStartMonth = prevQ * 3
      const qEndMonth = qStartMonth + 2
      const qEndDay = new Date(prevQYear, qEndMonth + 1, 0).getDate()
      return {
        start: `${prevQYear}-${String(qStartMonth + 1).padStart(2, '0')}-01`,
        end: `${prevQYear}-${String(qEndMonth + 1).padStart(2, '0')}-${String(qEndDay).padStart(2, '0')}`,
      }
    }
    case 'ytd': {
      return { start: `${year}-01-01`, end: fmt(now) }
    }
    case '2026': return { start: '2026-01-01', end: '2026-12-31' }
    case '2025': return { start: '2025-01-01', end: '2025-12-31' }
    default: {
      // Handle dynamic quarter values like Q1-2026, Q3-2025, etc.
      const quarterMatch = period.match(/^Q([1-4])-(\d{4})$/)
      if (quarterMatch) {
        const q = parseInt(quarterMatch[1])
        const y = parseInt(quarterMatch[2])
        const startMonth = (q - 1) * 3 // 0-indexed
        const endMonth = startMonth + 2
        const endDay = new Date(y, endMonth + 1, 0).getDate()
        return {
          start: `${y}-${String(startMonth + 1).padStart(2, '0')}-01`,
          end: `${y}-${String(endMonth + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
        }
      }
      // Handle dynamic year values
      const yearMatch = period.match(/^(\d{4})$/)
      if (yearMatch) {
        return { start: `${yearMatch[1]}-01-01`, end: `${yearMatch[1]}-12-31` }
      }
      return null
    }
  }
}

/**
 * Shared utility: filters trades by time period.
 * Closed trades filtered by closeDate (when the P&L was realized).
 * Open trades always shown regardless of period.
 * Handles both camelCase (frontend models) and snake_case (Supabase raw rows).
 */
export function filterTradesByPeriod(trades: any[], period: string): any[] {
  const range = getTimePeriodRange(period)
  if (!range) return trades

  return trades.filter((t: any) => {
    // Open trades always shown regardless of time period
    if (t.status === 'open') return true

    // Closed trades filtered by close date
    const closeDate = t.closeDate || t.close_date || null
    if (!closeDate) return false
    return closeDate >= range.start && closeDate <= range.end
  })
}
